import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import Blog from "./components/Blog";
import BlogPost from "./components/BlogPost";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Alert, AlertDescription } from "./components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "./components/ui/avatar";
import { Separator } from "./components/ui/separator";
import { ScrollArea } from "./components/ui/scroll-area";
import { 
  Scale, 
  Users, 
  MessageSquare, 
  CreditCard, 
  Settings, 
  LogOut,
  CheckCircle,
  Crown,
  Sparkles,
  Shield,
  FileText,
  Search,
  Calendar,
  BookOpen,
  X
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import CRMDemo from "./CRMDemo";
import CRMProfessional from "./CRMProfessional";
import AdminDashboard from './components/AdminDashboard';
import Syntaxi from './components/Syntaxi';
import './components/Syntaxi.css';
import CRMDashboard from './CRMDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Authentication Hook
const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // Check URL fragment for session_id from auth callback
      const fragment = window.location.hash.substring(1);
      const params = new URLSearchParams(fragment);
      const sessionId = params.get('session_id');
      
      if (sessionId) {
        // Clear fragment from URL
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Call profile API with session ID
        const response = await axios.get(`${API}/auth/profile`, {
          headers: { 'X-Session-ID': sessionId }
        });
        
        setUser(response.data.user);
      } else {
        // Check for Google OAuth token in URL
        const urlParams = new URLSearchParams(window.location.search);
        const googleToken = urlParams.get('token');
        if (googleToken) {
          localStorage.setItem('nomokratia_token', googleToken);
          window.history.replaceState({}, document.title, '/dashboard');
          const response = await axios.get(`${API}/auth/me`, {
            headers: { 'Authorization': `Bearer ${googleToken}` }
          });
          setUser(response.data.user || response.data);
          return;
        }
        // Check existing authentication
        const token = localStorage.getItem('nomokratia_token');
        if (!token) throw new Error('No token');
        const response = await axios.get(`${API}/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setUser(response.data.user || response.data);
      }
    } catch (error) {
      console.log('Δεν είστε συνδεδεμένος');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async () => {
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/auth/google/start`);
      window.location.href = response.data.redirect_url;
    } catch (error) {
      console.error('Αποτυχία σύνδεσης:', error);
    }
  };

  const logout = async () => {
    localStorage.removeItem('nomokratia_token');
    localStorage.removeItem('nomokratia_user');
    setUser(null);
    window.location.href = '/?logged_out=true';
  };

  return { user, loading, login, logout, checkAuthStatus };
};

// Learn More Page Component  
const LearnMorePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_ailegalpro/artifacts/wofbxh36_Untitled%20design%20%2821%29.png" 
              alt="ΝΟΜΟΚΡΑΤΙΑ Logo" 
              className="h-12 w-auto"
            />
          </Link>
          <Link to="/">
            <Button variant="outline" className="border-slate-900 text-slate-900 hover:bg-slate-50">
              Επιστροφή στην Αρχική
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto text-center max-w-4xl">
          <h1 className="text-5xl font-bold text-gray-900 mb-6 greek-font">
            Μάθετε περισσότερα για τις Υπηρεσίες μας
          </h1>
          <p className="text-xl text-gray-600 mb-12 leading-relaxed">
            Χτισμένο από δικηγόρους για δικηγόρους. Πρόσβαση σε 150.000+ έγγραφα (ΦΕΚ, νομολογία, υποδείγματα) 
            και το πιο προηγμένο CRM για νομικούς στην Ελλάδα.
          </p>

          {/* Benefits */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center text-lg text-gray-700 mb-16">
            <div className="flex items-center">
              <span className="text-2xl mr-3">✅</span>
              Χωρίς δέσμευση
            </div>
            <div className="flex items-center">
              <span className="text-2xl mr-3">⚡</span>
              Άμεση ενεργοποίηση
            </div>
            <div className="flex items-center">
              <span className="text-2xl mr-3">🔄</span>
              Ακύρωση οποτεδήποτε
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-900 greek-font">
            Υπηρεσίες της Πλατφόρμας AI NOMOKRATIA
          </h2>
          
          <div className="space-y-12">
            {/* Service 1 */}
            <Card className="border-l-4 border-slate-900 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-slate-900 greek-font flex items-center">
                  <span className="text-3xl mr-4">💬</span>
                  1. Βασική Νομική Συμβουλευτική
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-700">Παροχή απαντήσεων σε νομικά ερωτήματα με άμεση αναφορά σε ΦΕΚ, νομολογία και δικαστικές αποφάσεις.</p>
                <p className="text-gray-700">Η AI δημιουργεί αναλυτικές απαντήσεις με παραπομπές σε άρθρα νόμων και τυχόν ερμηνευτικές εγκυκλίους.</p>
                <p className="text-gray-700">Δυνατότητα συνοπτικής ή εκτενούς απάντησης, ανάλογα με τις ανάγκες του χρήστη.</p>
              </CardContent>
            </Card>

            {/* Service 2 */}
            <Card className="border-l-4 border-green-500 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-green-700 greek-font flex items-center">
                  <span className="text-3xl mr-4">🔍</span>
                  2. Ανάλυση Εγγράφων
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-700">Αυτόματη αναγνώριση και ανάγνωση νομικών εγγράφων (PDF, Word, εικόνες με OCR).</p>
                <p className="text-gray-700">Επισήμανση σημαντικών άρθρων, κενών ή πιθανών νομικών προβλημάτων.</p>
                <p className="text-gray-700">Παροχή προτάσεων βελτίωσης ή προσθήκης ώστε το έγγραφο να είναι πλήρως νόμιμο και θωρακισμένο.</p>
                <p className="text-gray-700">Ενσωμάτωση OpenAI για γλωσσική ανάλυση και νομικό context.</p>
              </CardContent>
            </Card>

            {/* Service 3 */}
            <Card className="border-l-4 border-purple-500 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-purple-700 greek-font flex items-center">
                  <span className="text-3xl mr-4">🌍</span>
                  3. Νομικές Μεταφράσεις
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-700">Εξειδικευμένες μεταφράσεις μεταξύ Ελληνικών – Αγγλικών – Γερμανικών.</p>
                <p className="text-gray-700">Διατήρηση της αυθεντικής νομικής ορολογίας (legal terms consistency).</p>
                <p className="text-gray-700">Αυτόματος έλεγχος για αμφιλεγόμενες φράσεις ή λάθη που μπορεί να αλλάξουν τη νομική ισχύ.</p>
                <p className="text-gray-700">Εφαρμογή διπλού ελέγχου AI + ανθρώπινης εποπτείας (προαιρετικά).</p>
              </CardContent>
            </Card>

            {/* Service 4 */}
            <Card className="border-l-4 border-orange-500 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-orange-700 greek-font flex items-center">
                  <span className="text-3xl mr-4">⚙️</span>
                  4. Δημιουργός Συμβάσεων
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-700">Αυτόματη σύνταξη συμβάσεων (μίσθωσης, εμπορικών, εργατικών, εταιρικών κ.λπ.).</p>
                <p className="text-gray-700">Η AI ενσωματώνει τυπικές ρήτρες, αναφορές σε ΦΕΚ και ειδικές προβλέψεις για τον κάθε κλάδο.</p>
                <p className="text-gray-700">Δυνατότητα παραμετροποίησης με βάση τον κλάδο, το αντικείμενο και τις ανάγκες του πελάτη.</p>
                <p className="text-gray-700">Έτοιμο νομικό template σε Word/PDF για άμεση χρήση.</p>
              </CardContent>
            </Card>

            {/* Service 5 */}
            <Card className="border-l-4 border-red-500 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-red-700 greek-font flex items-center">
                  <span className="text-3xl mr-4">📄</span>
                  5. Γεννήτρια Νομικών Εγγράφων
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-700">Δημιουργία δικογράφων, αιτήσεων, εξώδικων, αναφορών και άλλων επίσημων νομικών εγγράφων.</p>
                <p className="text-gray-700">Αυτόματη προσαρμογή στη σωστή νομική γλώσσα και τη διαδικασία του κάθε δικαστηρίου.</p>
                <p className="text-gray-700">Εισαγωγή στοιχείων πελάτη → άμεση εξαγωγή ολοκληρωμένου νομικού κειμένου.</p>
                <p className="text-gray-700">Ιδανικό εργαλείο για δικηγόρους που θέλουν εξοικονόμηση χρόνου.</p>
              </CardContent>
            </Card>

            {/* Service 6 */}
            <Card className="border-l-4 border-indigo-500 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-indigo-700 greek-font flex items-center">
                  <span className="text-3xl mr-4">🛡️</span>
                  6. Έλεγχος Συμμόρφωσης
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-700">Αυτόματος έλεγχος εγγράφων και διαδικασιών για GDPR, HORECA, eCommerce, εργατικό δίκαιο.</p>
                <p className="text-gray-700">Αναφορά σε άρθρα νόμου και οδηγίες συμμόρφωσης.</p>
                <p className="text-gray-700">AI Radar για εντοπισμό πιθανών παραβάσεων και δημιουργία λίστας ενεργειών για διόρθωση.</p>
                <p className="text-gray-700">Ιδανικό για εταιρείες που θέλουν προληπτικό compliance χωρίς υψηλό κόστος.</p>
              </CardContent>
            </Card>

            {/* Service 7 */}
            <Card className="border-l-4 border-pink-500 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-pink-700 greek-font flex items-center">
                  <span className="text-3xl mr-4">📝</span>
                  7. Περίληψη Εγγράφων
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-700">Δημιουργία σύντομης και σαφούς περίληψης μεγάλων νομικών εγγράφων.</p>
                <p className="text-gray-700">Ενσωματωμένος χάρτης νομικών αναφορών (links σε άρθρα & αποφάσεις).</p>
                <p className="text-gray-700">Ιδανικό για γρήγορη ενημέρωση πελατών ή παρουσίαση σε δικαστήριο.</p>
                <p className="text-gray-700">Δυνατότητα εξαγωγής σε executive summary για εταιρική χρήση.</p>
              </CardContent>
            </Card>

            {/* Service 8 */}
            <Card className="border-l-4 border-yellow-500 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-yellow-700 greek-font flex items-center">
                  <span className="text-3xl mr-4">⚡</span>
                  8. Ραντάρ Νομικών Κινδύνων
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-700">Παρακολούθηση της νομοθεσίας σε πραγματικό χρόνο (ΦΕΚ, Ευρωπαϊκή Ένωση).</p>
                <p className="text-gray-700">Ειδοποιήσεις για επικείμενες αλλαγές νόμων που επηρεάζουν τον κλάδο του πελάτη.</p>
                <p className="text-gray-700">Ανάλυση κινδύνων σε σύμβαση ή επιχειρηματικό σχέδιο.</p>
                <p className="text-gray-700">Προληπτική προστασία πριν δημιουργηθεί νομικό πρόβλημα.</p>
              </CardContent>
            </Card>

            {/* Service 9 */}
            <Card className="border-l-4 border-teal-500 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-teal-700 greek-font flex items-center">
                  <span className="text-3xl mr-4">📋</span>
                  9. Συμβολαιογραφικός Βοηθός
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-700">Εξειδίκευση στο ελληνικό συμβολαιογραφικό & κτηματολογικό δίκαιο.</p>
                <p className="text-gray-700">Αυτόματη προετοιμασία πράξεων, πληρεξουσίων, συμβολαιογραφικών πράξεων μεταβίβασης.</p>
                <p className="text-gray-700">AI υποστήριξη για κτηματολόγιο & υποθηκοφυλακεία.</p>
                <p className="text-gray-700">Σύνδεση με δημόσιες βάσεις δεδομένων για αυτοματοποιημένο έλεγχο ακινήτων.</p>
              </CardContent>
            </Card>

            {/* Service 10 */}
            <Card className="border-l-4 border-cyan-500 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-cyan-700 greek-font flex items-center">
                  <span className="text-3xl mr-4">👥</span>
                  10. CRM Πελατών
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-700">Ενσωματωμένο Legal CRM για διαχείριση πελατών, υποθέσεων και εγγράφων.</p>
                <p className="text-gray-700">Αυτόματη συσχέτιση υποθέσεων – εγγράφων – ραντεβού.</p>
                <p className="text-gray-700">Ειδοποιήσεις AI για deadlines και δικαστικές προθεσμίες.</p>
                <p className="text-gray-700">Δυνατότητα ασφαλούς cloud αποθήκευσης με GDPR compliance.</p>
              </CardContent>
            </Card>

            {/* Service 11 */}
            <Card className="border-l-4 border-slate-500 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-slate-700 greek-font flex items-center">
                  <span className="text-3xl mr-4">📚</span>
                  11. Αναζήτηση σε 150.000 Νόμων & Αποφάσεις
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-700">Πλήρης πρόσβαση σε 150.000+ ελληνικούς νόμους & δικαστικές αποφάσεις.</p>
                <p className="text-gray-700">Έξυπνα φίλτρα αναζήτησης (ημερομηνία, δικαστήριο, άρθρο νόμου, λέξη-κλειδί).</p>
                <p className="text-gray-700">Διασύνδεση με ECLI ευρωπαϊκή νομολογία.</p>
              </CardContent>
            </Card>
          </div>

          {/* CTA Section */}
          <div className="text-center mt-16">
            <h3 className="text-3xl font-bold text-gray-900 mb-8 greek-font">
              Έτοιμοι να ξεκινήσετε;
            </h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button className="bg-gradient-to-r from-slate-800 to-slate-900 hover:from-blue-600 hover:to-green-600 text-white text-lg px-8 py-3">
                  🎁 Δωρεάν δοκιμή 14 ημερών
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50 text-lg px-8 py-3">
                  Επιστροφή στην Αρχική
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white text-slate-800 py-12 px-4 border-t border-slate-200">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ailegalpro/artifacts/600ytiyy_Untitled%20design%20%2822%29.png" 
                  alt="ΝΟΜΟΚΡΑΤΙΑ Logo" 
                  className="h-8 w-auto"
                />
              </div>
              <p className="text-slate-400">
                Η επαγγελματική νομική πλατφόρμα σας για σύγχρονα δικηγορικά γραφεία.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 greek-font text-slate-800">Υποστήριξη</h4>
              <div className="text-slate-400">
                <p className="flex items-center"><span className="mr-2">📧</span>info@nomokratia.ai</p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4 greek-font text-slate-800">Νομικά</h4>
              <ul className="space-y-2 text-slate-400">
                <li><PrivacyPolicyModal /></li>
                <li><TermsOfServiceModal /></li>
                <li><a href="mailto:info@nomokratia.ai" className="text-slate-500 hover:text-slate-900 transition-colors">📧 Επικοινωνία</a></li>
              </ul>
            </div>
          </div>
          <Separator className="my-8 bg-slate-200" />
          <div className="text-center max-w-3xl mx-auto mb-6">
            <div style={{background:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:10,padding:'12px 16px',fontSize:13,color:'#1E3A8A',fontWeight:500,lineHeight:1.6}}>
              🤖 Η πλατφόρμα ΝΟΜΟΚΡΑΤΙΑ χρησιμοποιεί τεχνητή νοημοσύνη (AI). Το παραγόμενο περιεχόμενο είναι ενημερωτικό, δημιουργείται από AI και απαιτεί έλεγχο από αδειούχο δικηγόρο. (Κανονισμός ΕΕ 2024/1689 – Άρθρο 50)
            </div>
          </div>
          <div className="text-center text-slate-400">
            <p>&copy; 2026 ΝΟΜΟΚΡΑΤΙΑ by PROTOS AI Agency IKE. Όλα τα δικαιώματα διατηρούνται.</p>
            <p className="text-slate-400 text-xs mt-2">PROTOS AI Agency IKE · Αρχαιολογικού Μουσείου 17, 54640 Θεσσαλονίκη · ΑΦΜ 802208607 · ΓΕΜΗ 172146906000</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Register Modal Component
const RegisterModal = ({ isOpen, onClose, onSwitchToLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [registerSuccess, setRegisterSuccess] = useState(false);

  const handleGoogleRegister = async () => {
    setIsLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/auth/google/start`);
      window.location.href = response.data.redirect_url;
    } catch (error) {
      console.error('Google register failed:', error);
      setMessage({ type: 'error', text: 'Google login αποτυχία.' });
    } finally { setIsLoading(false); }
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    if (!acceptTerms) { alert('Παρακαλώ αποδεχτείτε τους όρους χρήσης'); return; }
    if (password !== passwordConfirm) { setMessage({type:'error',text:'Οι κωδικοί δεν ταιριάζουν.'}); return; }
    if (password.length < 8) { setMessage({type:'error',text:'Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες.'}); return; }
    setIsLoading(true);
    setMessage(null);
    try {
      await axios.post(`${API}/auth/email/register`, { email, password, first_name: '', last_name: '' });
      setRegisterSuccess(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Αποτυχία εγγραφής.';
      setMessage({ type: 'error', text: msg });
    } finally { setIsLoading(false); }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"><X className="h-6 w-6" /></button>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 greek-font">Δωρεάν Δοκιμή 14 Ημερών</h2>
          <p className="text-gray-500 text-sm mt-1">Χωρίς κάρτα, χωρίς δεσμεύσεις</p>
        </div>
        {registerSuccess ? (
          <div className="text-center py-8">
            <div style={{fontSize:64,marginBottom:16}}>✅</div>
            <h3 className="text-xl font-bold text-green-700 mb-4">Καλώς ήρθατε!</h3>
            <p className="text-gray-600 mb-6">Ξεκινήστε τη δωρεάν δοκιμή σας τώρα.</p>
            <button onClick={() => { setRegisterSuccess(false); onClose(); }} className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-lg text-lg">Ξεκινήστε →</button>
          </div>
        ) : (<>
        <button onClick={handleGoogleRegister} disabled={isLoading} className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold py-3 px-4 rounded-lg mb-4">
          <svg className="h-5 w-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          Εγγραφή με Google
        </button>
        <div className="relative mb-4"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300"></div></div><div className="relative flex justify-center text-sm"><span className="px-2 bg-white text-gray-500">ή</span></div></div>
        {message && (<div className={`mb-4 text-sm rounded p-3 ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>{message.text}</div>)}
        <form onSubmit={handleEmailRegister} className="space-y-3" noValidate>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Email *</label><Input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="your@email.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg" required /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Κωδικός *</label><Input type="password" value={password} onChange={(e)=>setPassword(e.target.value)} placeholder="Τουλάχιστον 8 χαρακτήρες" className="w-full px-3 py-2 border border-gray-300 rounded-lg" required /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Επιβεβαίωση Κωδικού *</label><Input type="password" value={passwordConfirm} onChange={(e)=>setPasswordConfirm(e.target.value)} placeholder="Επαναλάβετε τον κωδικό" className="w-full px-3 py-2 border border-gray-300 rounded-lg" required /></div>
          <label className="flex items-start space-x-3 cursor-pointer">
            <input type="checkbox" checked={acceptTerms} onChange={(e)=>setAcceptTerms(e.target.checked)} className="mt-1 h-4 w-4 text-slate-900 border-gray-300 rounded" required />
            <span className="text-xs text-gray-600">Αποδέχομαι τους <TermsOfServiceModal /> και την <PrivacyPolicyModal />. Κατανοώ ότι το ΝΟΜΟΚΡΑΤΙΑ παρέχει αποκλειστικά ενημερωτικές υπηρεσίες.</span>
          </label>
          <Button type="submit" disabled={isLoading || !acceptTerms} className="w-full bg-slate-900 hover:bg-slate-800 text-white py-3 rounded-lg">{isLoading ? 'Εγγραφή...' : 'Ξεκινήστε Δωρεάν →'}</Button>
        </form>
        </>)}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Έχετε ήδη λογαριασμό;
            <button 
              onClick={onSwitchToLogin}
              className="text-slate-900 hover:text-slate-900 cursor-pointer ml-1 bg-transparent border-none"
            >
              Συνδεθείτε
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

// Login Modal Component
const LoginModal = ({ isOpen, onClose, onSwitchToRegister }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null); // {type:'success'|'error', text:string}
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [resendEmail, setResendEmail] = useState("");

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/auth/google/start`);
      window.location.href = response.data.redirect_url;
    } catch (error) {
      console.error('Google login failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);
    try {
      const res = await axios.post(`${API}/auth/email/login`, { email, password });
      if (res.data.token) {
        localStorage.setItem('nomokratia_token', res.data.token);
        if (res.data.user) localStorage.setItem('nomokratia_user', JSON.stringify(res.data.user));
      }
      setMessage({ type: 'success', text: 'Επιτυχής σύνδεση.' });
      setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setMessage({ type: 'error', text: 'Παρακαλώ επιβεβαιώστε το email σας πριν τη σύνδεση.' });
      } else if (status === 401) {
        setMessage({ type: 'error', text: 'Μη έγκυρα στοιχεία εισόδου.' });
      } else {
        setMessage({ type: 'error', text: 'Αποτυχία σύνδεσης. Προσπαθήστε ξανά.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 relative">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
        >
          <X className="h-6 w-6" />
        </button>
        
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 greek-font">Σύνδεση</h2>
        </div>

        {/* Google Login Button */}
        

        

        {/* Email Login Form */}
        {message && (
          <div className={`mb-4 text-sm rounded p-3 ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {message.text}
          </div>
        )}
        <form onSubmit={handleEmailLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 greek-font">
              Email
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 greek-font">
              Κωδικός Πρόσβασης
            </label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            />
          </div>

          <Button 
            type="submit"
            disabled={isLoading}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white py-3 rounded-lg"
          >
            Σύνδεση με Email
          </Button>
        </form>

        {/* Footer Links */}
        <div className="mt-6 text-center space-y-2">
          <p className="text-sm text-slate-900 hover:text-slate-900 cursor-pointer" onClick={() => setShowForgot(!showForgot)}>
            Ξεχάσατε τον κωδικό σας;
          </p>
          {showForgot && (
            <div className="mt-3 text-left space-y-3 p-3 border rounded bg-gray-50">
              <div>
                <label className="block text-xs text-gray-700 mb-1">Email για επαναφορά</label>
                <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="your@email.com" />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="border-slate-900 text-slate-900" onClick={async () => {
                  setIsLoading(true);
                  setMessage(null);
                  try {
                    await axios.post(`${API}/auth/email/request-password-reset`, { email: forgotEmail || email });
                    setMessage({ type: 'success', text: 'Αν υπάρχει λογαριασμός, θα λάβετε email επαναφοράς.' });
                  } catch {
                    setMessage({ type: 'error', text: 'Αποτυχία αιτήματος επαναφοράς.' });
                  } finally {
                    setIsLoading(false);
                  }
                }}>Αίτημα επαναφοράς</Button>
                <Button type="button" variant="outline" onClick={async () => {
                  setIsLoading(true);
                  setMessage(null);
                  try {
                    await axios.post(`${API}/auth/email/resend-verification`, { email: resendEmail || email });
                    setMessage({ type: 'success', text: 'Στάλθηκε email επιβεβαίωσης (αν υπάρχει λογαριασμός).' });
                  } catch {
                    setMessage({ type: 'error', text: 'Αποτυχία επανάληψης επιβεβαίωσης.' });
                  } finally {
                    setIsLoading(false);
                  }
                }}>Επανάληψη επιβεβαίωσης</Button>
              </div>
              <div>
                <label className="block text-xs text-gray-700 mb-1">Email για επανάληψη επιβεβαίωσης</label>
                <Input type="email" value={resendEmail} onChange={(e) => setResendEmail(e.target.value)} placeholder="your@email.com" />
              </div>
            </div>
          )}
          <p className="text-sm text-gray-600">
            Δεν έχετε λογαριασμό; 
            <button 
              onClick={onSwitchToRegister}
              className="text-slate-900 hover:text-slate-900 cursor-pointer ml-1 bg-transparent border-none"
            >
              Εγγραφείτε
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

const PrivacyPolicyModal = () => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className="text-slate-500 hover:text-slate-900 cursor-pointer">
          Πολιτική Απορρήτου (GDPR)
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto mt-2">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center greek-font">
            🔒 Πολιτική Απορρήτου (GDPR)
          </DialogTitle>
          <p className="text-center text-gray-600">Τελευταία ενημέρωση: 03/03/2026</p>
        </DialogHeader>
        
        <div className="space-y-6 text-sm">
          <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
            <h3 className="text-lg font-bold text-orange-800 mb-2">⚠️ ΣΗΜΑΝΤΙΚΗ ΣΗΜΕΙΩΣΗ</h3>
            <p className="text-orange-700">Αυτή η εφαρμογή παρέχει ΜΟΝΟ ενημερωτικές πληροφορίες και ΔΕΝ αποτελεί νομική συμβουλή. Για επίσημη νομική συμβουλευτική, ΠΑΝΤΑ συμβουλευτείτε επαγγελματία δικηγόρο.</p>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">1. Υπεύθυνος Επεξεργασίας</h3>
            <div className="bg-gray-50 border border-gray-200 p-4 rounded">
              <p className="font-bold">PROTOS AI Agency IKE</p>
              <p className="text-xs text-gray-500 mb-1">Ανάπτυξη & Ιδιοκτησία της πλατφόρμας ΝΟΜΟΚΡΑΤΙΑ</p>
              <p>Διεύθυνση: Αρχαιολογικού Μουσείου 17</p>
              <p>Ταχυδρομικός Κώδικας: 54640 Θεσσαλονίκη</p>
              <p>ΑΦΜ: 802208607 | ΓΕΜΗ: 172146906000</p>
              <p>Email: info@nomokratia.ai</p>
              <p>Website: www.nomokratia.ai</p>
              <p>Υπεύθυνος Προστασίας Δεδομένων: info@nomokratia.ai</p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">2. Συλλογή Προσωπικών Δεδομένων</h3>
            <p className="mb-3">Δεδομένα που συλλέγουμε:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Στοιχεία Λογαριασμού (ονοματεπώνυμο, email, τηλέφωνο, ΑΦΜ, διεύθυνση)</li>
              <li>Νομικές Ερωτήσεις</li>
              <li>Απαντήσεις AI</li>
              <li>Στοιχεία Πληρωμής μέσω Stripe</li>
              <li>Τεχνικά Δεδομένα (IP, browser, συσκευή)</li>
              <li>Cookies (session, προτιμήσεις)</li>
            </ul>
            <div className="mt-4">
              <h4 className="font-semibold mb-2">CRM Δεδομένα Δικηγόρων:</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>Στοιχεία Πελατών/Εντολέων (ονοματεπώνυμο, ΑΦΜ, ΑΜΚΑ, στοιχεία επικοινωνίας)</li>
                <li>Υποθέσεις & Δικόγραφα</li>
                <li>Προθεσμίες & Χρονοχρέωση</li>
                <li>Τιμολόγια & Οικονομικά Στοιχεία</li>
                <li>Ανεβασμένα Έγγραφα & Αρχεία</li>
              </ul>
            </div>
            <div className="bg-blue-50 border border-blue-200 p-4 rounded mt-4">
              <h4 className="font-bold text-blue-800 mb-2">⚠️ ΑΚΡΙΒΕΙΑ AI ΑΠΟΤΕΛΕΣΜΑΤΩΝ</h4>
              <p className="text-blue-700">Η πλατφόρμα αξιοποιεί τεχνητή νοημοσύνη (AI) για νομική αναζήτηση και ανάλυση σε 3.5M+ έγγραφα. Τα αποτελέσματα έχουν ακρίβεια άνω του 90%, αλλά <strong>ΔΕΝ εγγυώνται πλήρη ακρίβεια ή επικαιρότητα</strong>.</p>
              <p className="text-blue-700 mt-2"><strong>Ο δικηγόρος φέρει πάντα την ευθύνη</strong> να επαληθεύει τα αποτελέσματα, να ελέγχει την ισχύ νομοθεσίας και νομολογίας, και να μην στηρίζεται αποκλειστικά στις AI αναλύσεις για τη λήψη νομικών αποφάσεων.</p>
              <p className="text-blue-700 mt-2">Η PROTOS AI Agency IKE <strong>αποκλείει κάθε ευθύνη</strong> για ζημίες που τυχόν προκύψουν από τη χρήση των AI αποτελεσμάτων χωρίς ανεξάρτητη επαλήθευση από τον δικηγόρο.</p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">3. Σκοπός Επεξεργασίας</h3>
            <ul className="list-disc list-inside space-y-1">
              <li>Παροχή Υπηρεσιών: AI νομική αναζήτηση και CRM (ενημερωτικές μόνο)</li>
              <li>Διαχείριση Λογαριασμού</li>
              <li>Χρεώσεις μέσω Stripe</li>
              <li>Βελτίωση Υπηρεσιών</li>
              <li>Νομικές Υποχρεώσεις</li>
              <li>Ασφάλεια</li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">4. Νομική Βάση Επεξεργασίας (GDPR)</h3>
            <ul className="list-disc list-inside space-y-1">
              <li>Άρθρο 6(1)(a) – Συγκατάθεση</li>
              <li>Άρθρο 6(1)(b) – Εκτέλεση Σύμβασης</li>
              <li>Άρθρο 6(1)(c) – Νομική Υποχρέωση</li>
              <li>Άρθρο 6(1)(f) – Έννομο Συμφέρον</li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">5. Κοινοποίηση σε Τρίτους</h3>
            <ul className="list-disc list-inside mb-3 space-y-1">
              <li>Anthropic (ανωνυμοποιημένα δεδομένα για AI επεξεργασία)</li>
              <li>Stripe (πληρωμές)</li>
              <li>MongoDB (βάση δεδομένων, τοπικός server στη Γερμανία/EU)</li>
              <li>Hetzner (φιλοξενία, EU servers)</li>
              <li>Αρχές (μόνο με δικαστική απόφαση)</li>
            </ul>
            <div className="bg-red-50 border border-red-200 p-3 rounded">
              <p className="text-red-700 font-bold">⚠️ ΔΕΝ πουλάμε, ΔΕΝ νοικιάζουμε, ΔΕΝ μοιραζόμαστε δεδομένα για marketing.</p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">6. Διατήρηση Δεδομένων</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <ul className="list-disc list-inside space-y-1">
                <li>Στοιχεία Λογαριασμού: 2 χρόνια</li>
                <li>Νομικές Ερωτήσεις: 3 χρόνια</li>
                <li>Πληρωμές: 7 χρόνια</li>
              </ul>
              <ul className="list-disc list-inside space-y-1">
                <li>Logs: 6 μήνες</li>
                <li>Cookies: 14 ημέρες</li>
              </ul>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">7. Τα Δικαιώματά σας (GDPR)</h3>
            <p className="mb-3">Πρόσβαση, Διόρθωση, Διαγραφή, Περιορισμός, Φορητότητα, Αντίρρηση, Ανάκληση Συγκατάθεσης.</p>
            <div className="bg-slate-50 border border-slate-200 p-3 rounded">
              <p className="text-slate-900">📧 <strong>info@nomokratia.ai</strong> — απάντηση σε 30 ημέρες.</p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">8. Ασφάλεια Δεδομένων</h3>
            <p>TLS/SSL, AES-256, Bcrypt, monitoring, backups, EU servers.</p>
            <div className="mt-3">
              <h4 className="font-semibold mb-2">Απομόνωση Δεδομένων CRM:</h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Κάθε δικηγόρος βλέπει <strong>αποκλειστικά</strong> τα δικά του δεδομένα</li>
                <li>Πλήρης απομόνωση πελατών, υποθέσεων και εγγράφων ανά χρήστη</li>
                <li>Ο διαχειριστής της πλατφόρμας <strong>δεν έχει πρόσβαση</strong> στο περιεχόμενο των υποθέσεων</li>
                <li>Διαγραφή λογαριασμού = οριστική διαγραφή <strong>όλων</strong> των δεδομένων</li>
              </ul>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">9. Cookies και Tracking</h3>
            <p className="mb-2">Μόνο session και Stripe cookies.</p>
            <div className="bg-green-50 border border-green-200 p-3 rounded">
              <p className="text-green-700">❌ <strong>Όχι tracking / analytics / ads cookies.</strong></p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">10. Διεθνείς Μεταφορές</h3>
            <ul className="list-disc list-inside space-y-1">
              <li>Hetzner (EU servers, Germany)</li>
              <li>Anthropic (ΗΠΑ) με SCCs</li>
              <li>Stripe (PCI DSS certified)</li>
              <li>MongoDB (τοπικός server, Hetzner Germany/EU)</li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">11. Καταγγελίες</h3>
            <div className="bg-gray-50 border border-gray-200 p-4 rounded">
              <p className="font-bold">Αρχή Προστασίας Δεδομένων Προσωπικού Χαρακτήρα</p>
              <p>Κηφισίας 1-3, 11523 Αθήνα</p>
              <p>☎️ +30 210 6475600</p>
              <p>📧 contact@dpa.gr</p>
              <p>🌐 www.dpa.gr</p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">12. Ανακοίνωση – Προστασία Περιεχομένου</h3>
            <p className="mb-3">Τελευταία ενημέρωση: 19 Φεβρουαρίου 2026</p>
            
            <div className="bg-slate-50 border border-slate-200 p-4 rounded mb-4">
              <h4 className="font-bold text-slate-900 mb-2">📢 ΣΗΜΑΝΤΙΚΗ ΕΝΗΜΕΡΩΣΗ</h4>
              <p className="text-slate-900">Περιορισμός δωρεάν περιεχομένου για προστασία από AI scraping</p>
              <p className="text-slate-900 mt-2">Η πλατφόρμα AI ΝΟΜΟΚΡΑΤΙΑ περιορίζει το δωρεάν περιεχόμενο για μη συνδρομητικούς χρήστες.</p>
            </div>

            <h4 className="font-semibold mb-2">Λόγοι:</h4>
            <ul className="list-disc list-inside mb-4 space-y-1">
              <li>Προστασία Premium Συνδρομητών</li>
              <li>Αποτροπή AI Scraping σε 150.000+ νομοθετήματα & αποφάσεις</li>
              <li>Προστασία Δεδομένων</li>
            </ul>

            <div className="bg-yellow-50 border border-yellow-200 p-3 rounded mb-4">
              <p className="text-yellow-800">Η πλατφόρμα ΝΟΜΟΚΡΑΤΙΑ ΔΕΝ επιτρέπει scraping χωρίς άδεια.</p>
              <p className="text-yellow-800 mt-2">📄 Εταιρική άδεια παροχής δεδομένων δίνεται ΜΟΝΟ κατόπιν έγγραφης συμφωνίας.</p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">🚀 Νέες Βελτιώσεις Υπηρεσιών</h3>
            <ul className="list-disc list-inside space-y-1">
              <li>AI-Powered Legal Consultation (Anthropic Claude)</li>
              <li>Premium Νομολογία (ελληνικές & ευρωπαϊκές αποφάσεις)</li>
              <li>Advanced Search με γλωσσική ανάλυση</li>
              <li>Email Registration με πλήρη στοιχεία δικηγόρου</li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">💰 Νέα Πακέτα & Τιμές</h3>
            
            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="border border-slate-200 p-3 rounded">
                <h4 className="font-bold text-slate-900 mb-2">🆓 Δοκιμαστικό – Δωρεάν</h4>
                <p className="text-sm mb-2">14 ημέρες πλήρης πρόσβαση</p>
                <ul className="text-xs list-disc list-inside space-y-1">
                  <li>Πλήρες CRM</li>
                  <li>AI Αναζήτηση Νομολογίας</li>
                  <li>10 AI ερωτήματα/ημέρα</li>
                  <li>Χωρίς πιστωτική κάρτα</li>
                </ul>
              </div>

              <div className="border border-green-200 p-3 rounded">
                <h4 className="font-bold text-green-700 mb-2">💼 Επαγγελματικό – 29€/μήνα</h4>
                <p className="text-sm mb-2">Για ατομικά δικηγορικά γραφεία</p>
                <ul className="text-xs list-disc list-inside space-y-1">
                  <li>Απεριόριστα AI ερωτήματα</li>
                  <li>AADE / myDATA ενσωμάτωση</li>
                  <li>Τιμολόγηση</li>
                  <li>Conflict Check</li>
                  <li>Τηλεφωνική υποστήριξη</li>
                </ul>
              </div>

              <div className="border border-purple-200 p-3 rounded">
                <h4 className="font-bold text-purple-700 mb-2">🏢 Δικηγορικό Γραφείο – Κατόπιν επικοινωνίας</h4>
                <p className="text-sm mb-2">Για δικηγορικές εταιρείες</p>
                <ul className="text-xs list-disc list-inside space-y-1">
                  <li>Έως 10 χρήστες</li>
                  <li>API πρόσβαση</li>
                  <li>Custom Integrations</li>
                  <li>Προτεραιότητα υποστήριξης</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 p-4 rounded">
            <h3 className="text-lg font-bold text-red-800 mb-2">ΣΗΜΑΝΤΙΚΟΣ ΑΠΟΚΛΕΙΣΜΟΣ ΕΥΘΥΝΩΝ</h3>
            <p className="text-red-700">Η PROTOS AI Agency IKE ΔΕΝ παρέχει νομικές συμβουλές. Όλες οι απαντήσεις είναι ενημερωτικές.</p>
            <p className="text-red-700 mt-2 font-bold">Για επίσημη νομική συμβουλευτική, ΠΑΝΤΑ συμβουλευτείτε επαγγελματία δικηγόρο.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Terms of Service Modal Component
const TermsOfServiceModal = () => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className="text-slate-500 hover:text-slate-900 cursor-pointer">
          Όροι Χρήσης
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto mt-2">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center greek-font">
            ⚖️ Όροι Χρήσης & Νομικές Διευκρινίσεις
          </DialogTitle>
          <p className="text-center text-gray-600">Τελευταία ενημέρωση: 23/8/2025</p>
        </DialogHeader>
        
        <div className="space-y-6 text-sm">
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h3 className="text-lg font-bold text-red-800 mb-2">⚠️ ΣΗΜΑΝΤΙΚΕΣ ΝΟΜΙΚΕΣ ΔΙΕΥΚΡΙΝΙΣΕΙΣ ⚠️</h3>
            <p className="text-red-700">Με τη χρήση του ΝΟΜΟΚΡΑΤΙΑ αποδέχεστε πλήρως αυτούς τους όρους.</p>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">Άρθρο 1. ΑΠΟΥΣΙΑ ΝΟΜΙΚΩΝ ΣΥΜΒΟΥΛΩΝ</h3>
            <div className="bg-red-100 border border-red-300 p-3 rounded mb-3">
              <h4 className="font-bold text-red-800">🚨 ΘΕΜΕΛΙΩΔΗΣ ΑΠΟΚΛΕΙΣΜΟΣ ΕΥΘΥΝΩΝ:</h4>
              <p className="text-red-700">Η πλατφόρμα ΝΟΜΟΚΡΑΤΙΑ παρέχει ΑΠΟΚΛΕΙΣΤΙΚΑ γενικές πληροφοριακές υπηρεσίες και ΔΕΝ ΠΑΡΕΧΕΙ ΝΟΜΙΚΕΣ ΣΥΜΒΟΥΛΕΣ κατά την έννοια του νόμου περί νομικών υπηρεσιών.</p>
            </div>
            
            <h4 className="font-semibold mb-2">Ευρωπαϊκές Νομικές Διατάξεις:</h4>
            <ul className="list-disc list-inside mb-3 space-y-1">
              <li>Οδηγία ΕΕ 2006/123/ΕΚ (Οδηγία Υπηρεσιών): Η υπηρεσία μας εμπίπτει στις γενικές πληροφοριακές υπηρεσίες, όχι στη ρυθμιζόμενη νομική συμβουλευτική</li>
              <li>Κανονισμός ΕΕ 2016/679 (ΓΚΠΔ) Άρθρο 13: Διαφανής ενημέρωση για την επεξεργασία δεδομένων και τα όρια των υπηρεσιών</li>
              <li>Οδηγία 2000/31/ΕΚ (Οδηγία Ηλεκτρονικού Εμπορίου): Διευκρίνιση του τύπου υπηρεσίας για ηλεκτρονικές υπηρεσίες</li>
            </ul>

            <h4 className="font-semibold mb-2">Ελληνικές Νομικές Διατάξεις:</h4>
            <ul className="list-disc list-inside space-y-1">
              <li>Νόμος 4194/2013 (Κώδικας Δικηγόρων): Η νομική συμβουλευτική επιτρέπεται αποκλειστικά σε αδειοδοτημένους δικηγόρους</li>
              <li>Άρθρο 7 ΚΔικ: Απαγόρευση παροχής νομικών συμβουλών από μη-δικηγόρους</li>
              <li>Νόμος 2971/2001 (Προστασία Καταναλωτή): Προστασία από παραπλανητικές υπηρεσίες</li>
              <li>Αστικός Κώδικας Άρθρο 914: Αποκλεισμός ευθύνης για πληροφοριακές υπηρεσίες</li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">Άρθρο 2. ΠΕΡΙΟΡΙΣΜΟΙ ΣΥΣΤΗΜΑΤΟΣ ΤΕΧΝΗΤΗΣ ΝΟΗΜΟΣΥΝΗΣ</h3>
            <h4 className="font-semibold mb-2">Τεχνικοί Περιορισμοί:</h4>
            <ul className="list-disc list-inside mb-3 space-y-1">
              <li>Οι απαντήσεις που παράγονται από την AI μπορεί να περιέχουν σφάλματα, ανακρίβειες ή παρωχημένες πληροφορίες</li>
              <li>Το σύστημα δεν λαμβάνει υπόψη τις ατομικές περιστάσεις της υπόθεσής σας</li>
              <li>Οι νομικές διατάξεις υπόκεινται σε συνεχείς αλλαγές - η επικαιρότητα δεν εγγυάται</li>
              <li>Η AI δεν μπορεί να κατανοήσει πλήρως το νομικό πλαίσιο και τις αποχρώσεις</li>
            </ul>
            
            <h4 className="font-semibold mb-2">Δικαστικές Προεκδικαστικές Υποθέσεις:</h4>
            <p>Τα αναφερόμενα ECLI κρίσεις χρησιμεύουν μόνο για γενική ενημέρωση. Κάθε υπόθεση είναι μοναδική - παρελθούσες αποφάσεις δεν εγγυώνται μελλοντικές αποφάσεις.</p>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">Άρθρο 3. ΠΛΗΡΗΣ ΑΠΟΚΛΕΙΣΜΟΣ ΕΥΘΥΝΩΝ</h3>
            <div className="bg-red-100 border border-red-300 p-3 rounded mb-3">
              <h4 className="font-bold text-red-800">🛑 ΑΠΟΛΥΤΗ ΑΠΑΛΛΑΓΗ ΕΥΘΥΝΩΝ:</h4>
              <p className="text-red-700">Η PROTOS AI Agency IKE και η πλατφόρμα ΝΟΜΟΚΡΑΤΙΑ ΔΕΝ ΑΝΑΛΑΜΒΑΝΟΥΝ ΚΑΜΙΑ ΕΥΘΥΝΗ για:</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold mb-2">Άμεσες Ζημίες:</h4>
                <ul className="list-disc list-inside space-y-1">
                  <li>Λανθασμένες νομικές εκτιμήσεις</li>
                  <li>Χαμένες προθεσμίες ή ραντεβού</li>
                  <li>Χαμένες δικαστικές διαδικασίες</li>
                  <li>Οικονομικές απώλειες</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Έμμεσες Ζημίες:</h4>
                <ul className="list-disc list-inside space-y-1">
                  <li>Χαμένα κέρδη</li>
                  <li>Ζημίες φήμης</li>
                  <li>Παρεπόμενες ζημίες κάθε είδους</li>
                  <li>Κόστος ευκαιρίας</li>
                </ul>
              </div>
            </div>
            <p className="mt-3 text-sm"><strong>Νομική Βάση:</strong> Βασιζόμενοι στην Οδηγία ΕΕ 2000/31/ΕΚ Άρθρα 12-15 και στο ελληνικό αστικό δίκαιο (ΑΚ 914-918), ως καθαρή πληροφοριακή πλατφόρμα δεν αναλαμβάνουμε ευθύνη για τις αποφάσεις των χρηστών.</p>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">Άρθρο 4. ΕΠΑΓΓΕΛΜΑΤΙΚΗ ΝΟΜΙΚΗ ΣΥΜΒΟΥΛΕΥΤΙΚΗ ΑΠΑΡΑΙΤΗΤΗ</h3>
            <div className="bg-green-100 border border-green-300 p-3 rounded mb-3">
              <h4 className="font-bold text-green-800">✅ ΕΠΕΙΓΟΥΣΑ ΣΥΣΤΑΣΗ:</h4>
              <p className="text-green-700">Για όλα τα νομικά θέματα συμβουλευτείτε ΠΑΝΤΑ έναν αδειοδοτημένο δικηγόρο!</p>
            </div>
            
            <p className="mb-2">Η δικηγορική συμβουλευτική είναι ΑΠΑΡΑΙΤΗΤΗ σε:</p>
            <div className="grid md:grid-cols-3 gap-2">
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Δικαστικές διαδικασίες</li>
                <li>Σύνταξη συμβολαίων</li>
                <li>Διαζύγια</li>
                <li>Κληρονομικά θέματα</li>
              </ul>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Ποινικές διαδικασίες</li>
                <li>Εργατικό δίκαιο</li>
                <li>Ακίνητα</li>
                <li>Εταιρικό δίκαιο</li>
              </ul>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Φορολογικό δίκαιο</li>
                <li>Διαδικασίες πτώχευσης</li>
                <li>Διεθνή νομικά θέματα</li>
                <li>Σύνθετες νομικές υποθέσεις</li>
              </ul>
            </div>

            <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded">
              <h4 className="font-bold mb-2">🏛️ Επικοινωνίες για επαγγελματική βοήθεια:</h4>
              <ul className="text-sm space-y-1">
                <li>• Δικηγορικός Σύλλογος Αθηνών: www.dsa.gr | +30 210 3616939</li>
                <li>• Δικηγορικός Σύλλογος Θεσσαλονίκης: www.dst.gr | +30 2310 287820</li>
                <li>• Νομική βοήθεια ΕΕ: europa.eu/citizens/legal-help</li>
              </ul>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">Άρθρο 5. ΟΡΟΙ ΧΡΗΣΗΣ ΥΠΗΡΕΣΙΑΣ</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold mb-2 text-green-700">Επιτρεπόμενη Χρήση:</h4>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>Γενικές νομικές πληροφορίες για προσωπική εκπαίδευση</li>
                  <li>Πρώτος προσανατολισμός σε νομικά ερωτήματα</li>
                  <li>Έρευνα για νόμους και αποφάσεις</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2 text-red-700">Απαγορευμένη Χρήση:</h4>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>Αντικατάσταση δικηγορικής συμβουλευτικής</li>
                  <li>Βάση για νομικές αποφάσεις</li>
                  <li>Χρήση σε δικαστικές διαδικασίες ως απόδειξη</li>
                  <li>Εμπορική επαναχρησιμοποίηση χωρίς άδεια</li>
                  <li>Scraping ή αυτοματοποιημένη εξαγωγή δεδομένων</li>
                </ul>
              </div>
            </div>
            <p className="mt-3 text-sm"><strong>Συμβατικό Δίκαιο:</strong> Αυτοί οι όροι χρήσης διέπονται από το ελληνικό δίκαιο. Αρμόδια δικαστήρια είναι αυτά της Θεσσαλονίκης.</p>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">Άρθρο 6. ΠΡΟΣΤΑΣΙΑ ΔΕΔΟΜΕΝΩΝ & ΣΥΜΜΟΡΦΩΣΗ ΓΚΠΔ</h3>
            <p className="mb-2">Οι νομικές σας ερωτήσεις επεξεργάζονται σύμφωνα με:</p>
            <ul className="list-disc list-inside mb-3 space-y-1">
              <li>ΓΚΠΔ Άρθρο 6(1)(β): Εκτέλεση σύμβασης για παροχή υπηρεσίας</li>
              <li>ΓΚΠΔ Άρθρο 6(1)(στ): Νόμιμα συμφέροντα για βελτίωση υπηρεσίας</li>
              <li>Νόμος 4624/2019: Ελληνική εφαρμογή του ΓΚΠΔ</li>
            </ul>
            <p className="text-sm">🔒 <strong>Τα δικαιώματά σας:</strong> Πρόσβαση, διόρθωση, διαγραφή, περιορισμός, φορητότητα δεδομένων και αντίρρηση σύμφωνα με τα άρθρα 15-21 του ΓΚΠΔ.</p>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">Άρθρο 8. ΠΑΚΕΤΑ ΣΥΝΔΡΟΜΗΣ ΚΑΙ ΟΡΙΑ ΧΡΗΣΗΣ</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="border border-slate-200 p-3 rounded">
                <h4 className="font-bold text-slate-900">🆓 Δοκιμαστικό — Δωρεάν</h4>
                <p className="text-sm">14 ημέρες πλήρης πρόσβαση · 10 AI αναλύσεις/ημέρα · Χωρίς κάρτα</p>
              </div>
              <div className="border border-green-200 p-3 rounded">
                <h4 className="font-bold text-green-700">💼 Επαγγελματικό — 29 € / μήνα</h4>
                <p className="text-sm">Για δικηγόρους · Απεριόριστες AI αναλύσεις · AADE · Conflict Check</p>
              </div>
              <div className="border border-purple-200 p-3 rounded">
                <h4 className="font-bold text-purple-700">🏢 Δικηγορικό Γραφείο — Κατόπιν επικοινωνίας</h4>
                <p className="text-sm">Για γραφεία με 2+ δικηγόρους · API · Priority Support</p>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <h4 className="font-bold mb-2">💡 ΣΗΜΑΝΤΙΚΕΣ ΔΙΕΥΚΡΙΝΙΣΕΙΣ:</h4>
              <ul className="text-sm space-y-1">
                <li>• Οι τιμές του Εταιρικού πλάνου καθορίζονται κατόπιν ατομικής συμφωνίας</li>
                <li>• Ακύρωση συνδρομής ανά πάσα στιγμή — χωρίς δεσμεύσεις</li>
                <li>• Όλες οι τιμές και όρια υπόκεινται σε αλλαγές με προηγούμενη ειδοποίηση</li>
              </ul>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">Άρθρο 9. ΤΕΛΙΚΕΣ ΔΙΑΤΑΞΕΙΣ</h3>
            <p className="mb-2"><strong>Ρήτρα Διάσωσης:</strong> Εάν μεμονωμένες διατάξεις είναι άκυρες, η εγκυρότητα των υπόλοιπων διατάξεων παραμένει ανεπηρέαστη.</p>
            <p className="mb-4"><strong>Δικαίωμα Τροποποίησης:</strong> Διατηρούμε το δικαίωμα να τροποποιούμε αυτούς τους όρους οποτεδήποτε. Οι χρήστες θα ειδοποιούνται για αλλαγές μέσω email.</p>
            
            <div className="text-center p-4 bg-gray-100 border border-gray-300 rounded">
              <p className="font-bold">⚖️ Με τη χρήση της πλατφόρμας ΝΟΜΟΚΡΑΤΙΑ επιβεβαιώνετε ότι έχετε διαβάσει, κατανοήσει και αποδεχτεί πλήρως αυτούς τους όρους χρήσης. ⚖️</p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-3 greek-font">📞 Επικοινωνία & Νομικά Αιτήματα</h3>
            <div className="p-3 bg-gray-50 border border-gray-200 rounded">
              <p className="font-bold">PROTOS AI Agency IKE</p>
              <p>Αρχαιολογικού Μουσείου 17, 54640 Θεσσαλονίκη, Ελλάδα</p>
              <p>E-Mail: info@nomokratia.ai</p>
              <p>Website: www.nomokratia.ai</p>
              <p>ΑΦΜ: 802208607 | ΓΕΜΗ: 172146906000</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AnimatedCounter = ({ end, suffix = "", prefix = "", duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) { setStarted(true); } },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const steps = 60;
    const increment = end / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= end) { setCount(end); clearInterval(timer); }
      else { setCount(Math.floor(current)); }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [started, end, duration]);

  const formatNumber = (n) => {
    if (end >= 1000000) return (n / 1000000).toFixed(1) + "M";
    if (end >= 1000) return n.toLocaleString();
    return n.toString();
  };

  return <span ref={ref}>{prefix}{started ? formatNumber(count) : "0"}{suffix}</span>;
};

const LandingPage = ({ onLogin }) => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('logged_out') === 'true') {
      const msg = document.createElement('div');
      msg.style.cssText = 'position:fixed;top:20px;left:50%;transform:translateX(-50%);background:#059669;color:white;padding:12px 32px;border-radius:10px;font-size:14px;font-weight:600;z-index:99999;box-shadow:0 4px 12px rgba(0,0,0,0.15);';
      msg.textContent = 'Επιτυχής αποσύνδεση!';
      document.body.appendChild(msg);
      window.history.replaceState({}, '', '/');
      setTimeout(() => msg.remove(), 3000);
    }
  }, []);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isDemoChoiceOpen, setIsDemoChoiceOpen] = useState(false);
  const [notaryEmail, setNotaryEmail] = useState("");
  const [notarySubmitted, setNotarySubmitted] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState({});
  useEffect(() => { fetchSubscriptionPlans(); }, []);
  useEffect(() => { const p = new URLSearchParams(window.location.search); if (p.get('register') === 'true') { setIsRegisterModalOpen(true); window.history.replaceState({}, '', '/'); } }, []);
  const fetchSubscriptionPlans = async () => { try { const response = await axios.get(`${API}/subscriptions/plans`); setSubscriptionPlans(response.data); } catch (error) { console.error('Error:', error); } };
  const handleLoginModalClose = () => setIsLoginModalOpen(false);
  const handleRegisterModalClose = () => setIsRegisterModalOpen(false);
  const switchToRegister = () => { setIsLoginModalOpen(false); setIsRegisterModalOpen(true); };
  const switchToLogin = () => { setIsRegisterModalOpen(false); setIsLoginModalOpen(true); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <LoginModal isOpen={isLoginModalOpen} onClose={handleLoginModalClose} onSwitchToRegister={switchToRegister} />
      <RegisterModal isOpen={isRegisterModalOpen} onClose={handleRegisterModalClose} onSwitchToLogin={switchToLogin} />

      {/* Demo Choice Modal */}
      <Dialog open={isDemoChoiceOpen} onOpenChange={setIsDemoChoiceOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center greek-font">Επιλέξτε Demo</DialogTitle>
            <p className="text-center text-gray-500">Ποιο επάγγελμα σας αφορά;</p>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-6 mt-4">
            {/* Lawyer CRM Demo */}
            <div className="border-2 border-slate-900 rounded-xl p-6 text-center hover:bg-slate-50 transition-colors cursor-pointer group" onClick={() => { setIsDemoChoiceOpen(false); window.location.href = "/crm-demo"; }}>
              <div className="h-16 w-16 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <Scale className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 greek-font mb-2">Δικηγόροι</h3>
              <p className="text-gray-600 text-sm mb-4">Πλήρες CRM, AI Νομική Αναζήτηση, AADE, Τιμολόγηση, Conflict Check</p>
              <div className="inline-flex items-center text-slate-900 font-semibold">
                Δοκιμάστε Τώρα <span className="ml-2">→</span>
              </div>
            </div>

            {/* Notary CRM Demo - Coming Soon */}
            <div className="border-2 border-dashed border-blue-300 rounded-xl p-6 text-center bg-blue-50/50">
              <div className="h-16 w-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 greek-font mb-2">Συμβολαιογράφοι</h3>
              <p className="text-gray-600 text-sm mb-3">Διαχείριση Πράξεων, Κτηματολόγιο, ΓΕΜΗ, Διαθήκες, Πλειστηριασμοί</p>
              <Badge className="bg-blue-100 text-blue-700 mb-4">Σύντομα Διαθέσιμο</Badge>
              {!notarySubmitted ? (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-2">Εγγραφείτε για πρώτη πρόσβαση:</p>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="email@example.com"
                      value={notaryEmail}
                      onChange={(e) => setNotaryEmail(e.target.value)}
                      className="text-sm"
                    />
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (notaryEmail && notaryEmail.includes("@")) {
                          try {
                            await axios.post(API + "/early-access", { email: notaryEmail, type: "notary" });
                          } catch (err) {
                            window.location.href = "mailto:info@nomokratia.ai?subject=Early Access Συμβολαιογράφοι&body=Email: " + encodeURIComponent(notaryEmail);
                          }
                          setNotarySubmitted(true);
                        }
                      }}
                    >
                      Εγγραφή
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded">
                  <p className="text-green-700 text-sm font-medium">✓ Ευχαριστούμε! Θα σας ενημερώσουμε στο {notaryEmail}</p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <a href="/" onClick={(e) => { e.preventDefault(); window.scrollTo({top:0, behavior:"smooth"}); }} className="flex items-center cursor-pointer">
            <img src="https://customer-assets.emergentagent.com/job_ailegalpro/artifacts/wofbxh36_Untitled%20design%20%2821%29.png" alt="ΝΟΜΟΚΡΑΤΙΑ Logo" className="h-12 w-auto" />
          </a>
          <nav className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-gray-600 hover:text-slate-900 text-sm font-medium">Δυνατότητες</a>
            <a href="#database" className="text-gray-600 hover:text-slate-900 text-sm font-medium">Βάση Δεδομένων</a>
            <a href="#pricing" className="text-gray-600 hover:text-slate-900 text-sm font-medium">Τιμές</a>
            <a href="/blog" className="text-gray-600 hover:text-slate-900 text-sm font-medium">Blog</a>
            <Button variant="outline" onClick={() => setIsLoginModalOpen(true)} className="border-slate-900 text-slate-900 hover:bg-slate-100">Σύνδεση</Button>
            <Button onClick={() => setIsRegisterModalOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white">Δωρεάν Δοκιμή</Button>

          </nav>
          <div className="md:hidden flex items-center space-x-3">
            <Button variant="outline" onClick={() => setIsLoginModalOpen(true)} className="border-slate-900 text-slate-900">Σύνδεση</Button>
            <Button onClick={() => setIsRegisterModalOpen(true)} className="bg-slate-900 text-white">Δοκιμή</Button>
          </div>
        </div>
      </header>

      <section className="pt-10 pb-24 md:pt-24 md:pb-24 px-4">
        <div className="container mx-auto text-center max-w-5xl">
          <div className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-4 py-2 mb-4">
            <span className="text-slate-700 text-sm font-medium">🇪🇺 EU AI Pact Signatory</span>
            <span className="text-gray-400">|</span>
            <span className="text-slate-700 text-sm font-medium">🇬🇷 Ελληνική Κυριαρχία AI</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 greek-font leading-tight">
            Η μόνη ελληνική AI που<br />δουλεύει σαν ομάδα<br />εξειδικευμένων δικηγόρων.
          </h1>
          <p className="text-xl text-gray-500 mb-4 max-w-3xl mx-auto">
            AI νομική αναζήτηση σε 3.5+ εκατομμύρια έγγραφα, σύνταξη εγγράφων, AI Insights — και με Premium: CRM, AADE, τιμολόγηση, conflict check.
          </p>
          <p className="text-lg text-gray-400 mb-10">Δοκιμάστε δωρεάν για 14 ημέρες — χωρίς κάρτα.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Button onClick={() => setIsRegisterModalOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white text-lg px-10 py-6 rounded-xl shadow-lg">
              Ξεκινήστε Δωρεάν →
            </Button>

          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center"><div className="text-4xl font-bold text-slate-900"><AnimatedCounter end={3500000} suffix="+" /></div><div className="text-sm text-gray-500 mt-1">Νομικά Έγγραφα</div></div>
            <div className="text-center"><div className="text-4xl font-bold text-slate-900"><AnimatedCounter end={11} /></div><div className="text-sm text-gray-500 mt-1">CRM Modules</div></div>
            <div className="text-center"><div className="text-4xl font-bold text-slate-900"><AnimatedCounter end={15} suffix="+" /></div><div className="text-sm text-gray-500 mt-1">Πηγές Δικαίου</div></div>
            <div className="text-center"><div className="text-4xl font-bold text-slate-900"><AnimatedCounter end={24} suffix="/7" /></div><div className="text-sm text-gray-500 mt-1">AI Υποστήριξη</div></div>
          </div>
        </div>
      </section>

      <section id="features" className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 greek-font mb-4">Ό,τι χρειάζεται ένα σύγχρονο δικηγορικό γραφείο</h2>
            <p className="text-lg text-gray-500">Αντικαταστήστε 5+ εργαλεία με μία πλατφόρμα</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader><div className="h-12 w-12 bg-slate-100 rounded-xl flex items-center justify-center mb-3"><Users className="h-6 w-6 text-slate-900" /></div><CardTitle className="greek-font text-xl">Πλήρες CRM</CardTitle></CardHeader>
              <CardContent><p className="text-gray-600">Πελάτες, υποθέσεις, προθεσμίες, χρονοχρέωση, τιμολόγια — πλήρης διαχείριση γραφείου σε ένα μέρος.</p><div className="flex flex-wrap gap-2 mt-4"><Badge variant="outline">Πελάτες</Badge><Badge variant="outline">Υποθέσεις</Badge><Badge variant="outline">Προθεσμίες</Badge><Badge variant="outline">Ώρες</Badge><Badge variant="outline">Τιμολόγια</Badge></div></CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader><div className="h-12 w-12 bg-green-100 rounded-xl flex items-center justify-center mb-3"><Search className="h-6 w-6 text-green-600" /></div><CardTitle className="greek-font text-xl">AI Νομική Αναζήτηση</CardTitle></CardHeader>
              <CardContent><p className="text-gray-600">Αναζήτηση σε 3.5+ εκατομμύρια νομικά έγγραφα — νομολογία ΣτΕ, Αρείου Πάγου, Διαύγεια, EUR-Lex, ΦΕΚ, AADE.</p><div className="flex flex-wrap gap-2 mt-4"><Badge variant="outline">Νομολογία</Badge><Badge variant="outline">ΦΕΚ</Badge><Badge variant="outline">Διαύγεια</Badge><Badge variant="outline">EUR-Lex</Badge></div></CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader><div className="h-12 w-12 bg-orange-100 rounded-xl flex items-center justify-center mb-3"><CreditCard className="h-6 w-6 text-orange-600" /></div><CardTitle className="greek-font text-xl">AADE & Τιμολόγηση</CardTitle></CardHeader>
              <CardContent><p className="text-gray-600">Αυτόματη σύνδεση myDATA, ηλεκτρονική τιμολόγηση, παρακολούθηση πληρωμών και φορολογική συμμόρφωση.</p><div className="flex flex-wrap gap-2 mt-4"><Badge variant="outline">myDATA</Badge><Badge variant="outline">e-Τιμολόγια</Badge><Badge variant="outline">ΦΠΑ</Badge></div></CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader><div className="h-12 w-12 bg-purple-100 rounded-xl flex items-center justify-center mb-3"><Sparkles className="h-6 w-6 text-purple-600" /></div><CardTitle className="greek-font text-xl">AI Insights</CardTitle></CardHeader>
              <CardContent><p className="text-gray-600">Αυτόματη ανάλυση υποθέσεων, πρόβλεψη επιτυχίας, νομική συμβουλευτική και σύνταξη εγγράφων με τεχνητή νοημοσύνη.</p><div className="flex flex-wrap gap-2 mt-4"><Badge variant="outline">Ανάλυση</Badge><Badge variant="outline">Πρόβλεψη</Badge><Badge variant="outline">Σύνταξη</Badge></div></CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader><div className="h-12 w-12 bg-red-100 rounded-xl flex items-center justify-center mb-3"><Shield className="h-6 w-6 text-red-600" /></div><CardTitle className="greek-font text-xl">Σύγκρουση Συμφερόντων</CardTitle></CardHeader>
              <CardContent><p className="text-gray-600">Αυτοματοποιημένος έλεγχος σύγκρουσης συμφερόντων πριν αναλάβετε νέα υπόθεση. Πλήρης κωδικοποίηση δεοντολογίας.</p><div className="flex flex-wrap gap-2 mt-4"><Badge variant="outline">Auto-Check</Badge><Badge variant="outline">Δεοντολογία</Badge></div></CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader><div className="h-12 w-12 bg-teal-100 rounded-xl flex items-center justify-center mb-3"><FileText className="h-6 w-6 text-teal-600" /></div><CardTitle className="greek-font text-xl">Νομικά Έγγραφα</CardTitle></CardHeader>
              <CardContent><p className="text-gray-600">AI-powered σύνταξη δικογράφων, συμβάσεων, αιτήσεων. Αυτόματη εισαγωγή νομικών αναφορών και παραπομπών.</p><div className="flex flex-wrap gap-2 mt-4"><Badge variant="outline">Δικόγραφα</Badge><Badge variant="outline">Συμβάσεις</Badge><Badge variant="outline">Αιτήσεις</Badge></div></CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ── SYNTAXI HERO SECTION ── */}
      {/* ── TRUST TICKER BANNER ── */}
      <section className="relative overflow-hidden" style={{background:'linear-gradient(135deg, #1B3A5C 0%, #0F2440 100%)'}}>
        <div style={{padding:'18px 0'}}>
          <div className="flex items-center" style={{animation:'ticker 25s linear infinite',whiteSpace:'nowrap',display:'inline-flex'}}>
            {[0,1].map(copy => (
              <div key={copy} className="flex items-center" style={{paddingRight:'60px'}}>
                {[
                  { icon: "🇪🇺", label: "GDPR Compliant", sub: "Πλήρης Συμμόρφωση ΓΚΠΔ" },
                  { icon: "🛡️", label: "EU Data Centers", sub: "100% Ευρωπαϊκά Δεδομένα" },
                  { icon: "🔒", label: "ISO 27001 Ready", sub: "Ασφάλεια Δεδομένων" },
                  { icon: "🇪🇺", label: "EU AI Act Compliant", sub: "Κανονισμός ΤΝ 2024/1689" },
                  { icon: "🚫", label: "No Model Training", sub: "Τα δεδομένα σας ΔΕΝ εκπαιδεύουν AI" },
                  { icon: "⚖️", label: "EU AI Pact Signatory", sub: "Υπογραφή Ευρωπαϊκού Συμφώνου AI" },
                ].map((b, i) => (
                  <div key={`${copy}-${i}`} className="flex items-center" style={{marginRight:'50px'}}>
                    <span style={{fontSize:'28px',marginRight:'12px'}}>{b.icon}</span>
                    <div>
                      <div style={{color:'#FFF',fontWeight:700,fontSize:'15px',lineHeight:'1.2',letterSpacing:'0.3px'}}>{b.label}</div>
                      <div style={{color:'#94A3B8',fontSize:'12px',lineHeight:'1.3'}}>{b.sub}</div>
                    </div>
                    {i < 5 && <div style={{width:'1px',height:'32px',background:'rgba(255,255,255,0.15)',marginLeft:'50px'}}></div>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
        <style>{`
          @keyframes ticker {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}</style>
      </section>

      <section id="nomologia-showcase" className="py-20 px-4 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 rounded-full px-4 py-1 text-sm font-semibold mb-6">
                ⚖️ 3.5+ εκατομμύρια νομικά έγγραφα σε δευτερόλεπτα
              </div>
              <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 greek-font mb-4">
                🔍 AI Νομολογιακή Αναζήτηση
              </h2>
              <p className="text-base sm:text-xl text-gray-600 mb-6">
                Γράψτε το ερώτημά σας σε φυσική γλώσσα. Η NOMOKRATIA.AI αναζητά σε Άρειο Πάγο, ΣτΕ, Διαύγεια, EUR-Lex, ΦΕΚ, ΑΑΔΕ — και σας δίνει τεκμηριωμένη νομική ανάλυση με παραπομπές.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  "Αναζήτηση σε φυσική γλώσσα — χωρίς νομική ορολογία",
                  "3.5M+ έγγραφα: Άρειος Πάγος, ΣτΕ, Διαύγεια, EUR-Lex, ΦΕΚ",
                  "AI ανάλυση με υπαγωγή πραγματικών στο νομικό πλαίσιο",
                  "Streaming απάντηση σε πραγματικό χρόνο",
                  "Παραπομπές σε συγκεκριμένα άρθρα και αποφάσεις",
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="h-5 w-5 bg-slate-800 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <span className="text-gray-700">{f}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:w-1/2 w-full">
              <div className="bg-white rounded-2xl shadow-xl sm:shadow-2xl p-4 sm:p-6 border border-slate-200">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-3 w-3 rounded-full bg-red-400"></div>
                  <div className="h-3 w-3 rounded-full bg-yellow-400"></div>
                  <div className="h-3 w-3 rounded-full bg-green-400"></div>
                  <span className="ml-2 text-xs text-gray-400 font-mono hidden sm:inline">nomokratia.ai — AI Νομολογία</span>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600 space-y-3 leading-relaxed">
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <span className="text-gray-400 text-xs">Ερώτηση:</span>
                    <div className="text-gray-800 mt-1">Καταγγελία σύμβασης εργασίας λόγω οικονομοτεχνικών — δικαιούται αποζημίωση;</div>
                  </div>
                  <div className="text-blue-600 font-bold text-xs">🔍 Βρέθηκαν 12 σχετικά έγγραφα σε 1.2 δευτ.</div>
                  <div className="border-l-4 border-slate-800 pl-3 py-1">
                    <div className="font-bold text-slate-800 text-xs">📄 ΑΠ 234/2023 — Τμήμα Β1</div>
                    <div className="text-gray-600 text-xs mt-1">Κατά τη διάταξη του άρθρου 5 του Ν. 3198/1955, η καταγγελία σύμβασης εργασίας αορίστου χρόνου θεωρείται έγκυρη μόνο εφόσον...</div>
                  </div>
                  <div className="border-l-4 border-blue-400 pl-3 py-1">
                    <div className="font-bold text-slate-800 text-xs">📄 ΣτΕ 1456/2022</div>
                    <div className="text-gray-600 text-xs mt-1">Ο εργοδότης υποχρεούται σε καταβολή αποζημίωσης βάσει του Ν.2112/1920, ανεξαρτήτως του λόγου καταγγελίας...</div>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
                    <div className="text-green-800 text-xs font-bold">✅ AI Νομική Εκτίμηση</div>
                    <div className="text-green-700 text-xs mt-1">Βάσει Ν.2112/1920 και ΑΠ 234/2023, ο εργαζόμενος δικαιούται πλήρη αποζημίωση. Η προθεσμία διεκδίκησης είναι 6 μήνες...</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

            <section id="syntaxi" className="py-20 px-4 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 rounded-full px-4 py-1 text-sm font-semibold mb-6">
                ✨ Αποκλειστικό — δεν υπάρχει σε κανένα άλλο ελληνικό εργαλείο
              </div>
              <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 greek-font mb-4">
                ✍️ AI Σύνταξη Νομικών Εγγράφων
              </h2>
              <p className="text-base sm:text-xl text-gray-600 mb-6">
                Περιγράψτε την υπόθεση. Η NOMOKRATIA.AI βρίσκει αυτόματα τη σχετική νομολογία και συντάσσει πλήρες νομικό έγγραφο με inline παραπομπές σε πραγματικές αποφάσεις Αρείου Πάγου.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  "Αγωγή, Εξώδικο, Υπόμνημα, Έφεση, Ασφαλιστικά Μέτρα",
                  "Αυτόματη αναζήτηση νομολογίας — 40.000+ αποφάσεις ΑΠ",
                  "Inline παραπομπές [📄 ΑΠ 1067/2014] σε κάθε επιχείρημα",
                  "Επεξεργασία, εκτύπωση, PDF, email — άμεσα έτοιμο",
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="h-5 w-5 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <span className="text-gray-700">{f}</span>
                  </div>
                ))}
              </div>
              
            </div>
            <div className="lg:w-1/2 w-full">
              <div className="bg-white rounded-2xl shadow-xl sm:shadow-2xl p-4 sm:p-6 border border-blue-100">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-3 w-3 rounded-full bg-red-400"></div>
                  <div className="h-3 w-3 rounded-full bg-yellow-400"></div>
                  <div className="h-3 w-3 rounded-full bg-green-400"></div>
                  <span className="ml-2 text-xs text-gray-400 font-mono hidden sm:inline">nomokratia.ai — Σύνταξη Αγωγής</span>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 font-mono text-xs text-gray-600 space-y-1 leading-relaxed">
                  <div className="text-blue-600 font-bold">🔍 Βρέθηκαν 6 αποφάσεις Αρείου Πάγου...</div>
                  <div className="text-gray-400">━━━━━━━━━━━━━━━━━━━━━━━━</div>
                  <div className="font-serif text-gray-800 text-sm leading-relaxed">
                    <div className="font-bold">ΠΡΟΣ ΤΟ ΜΟΝΟΜΕΛΕΣ ΠΡΩΤΟΔΙΚΕΙΟ</div>
                    <div className="font-bold">ΘΕΣΣΑΛΟΝΙΚΗΣ</div>
                    <br/>
                    <div>Κατά την πάγια νομολογία</div>
                    <div><span className="bg-blue-100 text-blue-700 px-1 rounded">[📄 ΑΠ 1067/2014]</span> η μη</div>
                    <div>καταβολή μισθωμάτων συνιστά</div>
                    <div>ουσιώδη παράβαση...</div>
                    <br/>
                    <div>Επίσης <span className="bg-blue-100 text-blue-700 px-1 rounded">[📄 ΑΠ 612/2006]</span></div>
                    <div>ο εκμισθωτής δικαιούται...</div>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  {["✏️ Επεξεργασία","📄 PDF","📧 Email","🖨️ Εκτύπωση"].map(b => (
                    <span key={b} className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-lg">{b}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Maria AI Section */}
      <section id="maria-ai" className="py-20 px-4 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-bold text-white greek-font mb-6">{"Γνωρίστε τη Μαρία"}</h2>
            <p className="text-2xl text-blue-300 font-semibold mb-4">{"Η ψηφιακή γραμματέας σας. 24 ώρες, 7 ημέρες."}</p>
            <p className="text-lg text-slate-300 max-w-3xl mx-auto leading-relaxed">{"Μιλήστε της. Δεν χρειάζεται πληκτρολόγιο. Οδηγείτε προς το δικαστήριο και της λέτε: «Φτιάξε μου το υπόμνημα». Μέχρι να φτάσετε, είναι έτοιμο."}</p>
          </div>
          <div className="bg-slate-800/40 border border-slate-700 rounded-2xl p-8 mb-10">
            <p className="text-slate-300 text-center text-lg leading-relaxed">{"Η Μαρία είναι μια πραγματική γραμματέας που δεν κοιμάται ποτέ. Αναζητά νομολογία. Συντάσσει έγγραφα. Ελέγχει συγκρούσεις συμφερόντων. Εκδίδει τιμολόγια και τα ανεβάζει στην ΑΑΔΕ. Παρακολουθεί προθεσμίες. Στέλνει email. Όλα με μία φωνητική εντολή."}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <div className="bg-slate-800/60 border border-slate-700 rounded-2xl overflow-hidden">
              <div className="aspect-video bg-black"><video className="w-full h-full object-cover" controls playsInline preload="metadata"><source src="/videos/maria1video.mp4" type="video/mp4" /></video></div>
            </div>
            <div className="bg-slate-800/60 border border-slate-700 rounded-2xl overflow-hidden">
              <div className="aspect-video bg-black"><video className="w-full h-full object-cover" controls playsInline preload="metadata"><source src="/videos/maria2video.mp4" type="video/mp4" /></video></div>
            </div>
          </div>
          <div className="text-center">
            <p className="text-slate-400 text-sm mb-6">{"Διαθέσιμη ως premium πρόσθετο στη συνδρομή σας. Επιλέξτε εμφάνιση, φύλο και φωνή."}</p>
            <a href="mailto:info@ai-agency.gr?subject=Maria AI" className="inline-block bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-bold px-10 py-4 rounded-xl text-lg transition-all shadow-lg shadow-blue-500/25">{"Επικοινωνήστε μαζί μας"}</a>
          </div>
        </div>
      </section>

      <section id="database" className="py-20 px-4 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white greek-font mb-4">Η μεγαλύτερη ελληνική νομική βάση δεδομένων</h2>
            <p className="text-xl text-slate-300">3.5+ εκατομμύρια έγγραφα από 15+ πηγές δικαίου</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6"><div className="text-3xl font-bold text-blue-400 mb-1">3,369,458</div><div className="text-slate-300 font-medium">Διαύγεια</div><div className="text-slate-500 text-sm">Αποφάσεις Δημοσίου</div></div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6"><div className="text-3xl font-bold text-blue-400 mb-1">41,640</div><div className="text-slate-300 font-medium">EUR-Lex</div><div className="text-slate-500 text-sm">Ευρωπαϊκή Νομοθεσία (Ελληνικά)</div></div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6"><div className="text-3xl font-bold text-blue-400 mb-1">40,000+</div><div className="text-slate-300 font-medium">Άρειος Πάγος</div><div className="text-slate-500 text-sm">Αποφάσεις 1990-2025</div></div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6"><div className="text-3xl font-bold text-blue-400 mb-1">20,000+</div><div className="text-slate-300 font-medium">Συμβούλιο Επικρατείας</div><div className="text-slate-500 text-sm">ΣτΕ Αποφάσεις</div></div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6"><div className="text-3xl font-bold text-blue-400 mb-1">5,320</div><div className="text-slate-300 font-medium">Διοικητικά Δικαστήρια</div><div className="text-slate-500 text-sm">Πρωτόδικες Αποφάσεις</div></div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6"><div className="text-3xl font-bold text-blue-400 mb-1">4,373</div><div className="text-slate-300 font-medium">AADE</div><div className="text-slate-500 text-sm">Εγκύκλιοι & Αποφάσεις</div></div>
          </div>
          <div className="text-center mt-12">
            <p className="text-slate-400">+ ΦΕΚ, Βουλή, Ελεγκτικό Συνέδριο, Κώδικες Νόμων και πολλά ακόμα</p>
            <p className="text-slate-500 text-sm mt-2">Ενημέρωση σε πραγματικό χρόνο — νέες αποφάσεις κάθε ημέρα</p>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="flex flex-wrap justify-center items-center gap-8 mb-8">
            <div className="flex items-center gap-2 text-gray-500"><Shield className="h-5 w-5" /><span className="text-sm font-medium">EU AI Pact Signatory</span></div>
            <div className="flex items-center gap-2 text-gray-500"><span className="text-sm font-medium">🇪🇺 Registered EU Expert</span></div>
            <div className="flex items-center gap-2 text-gray-500"><span className="text-sm font-medium">🔒 GDPR Compliant</span></div>
            <div className="flex items-center gap-2 text-gray-500"><span className="text-sm font-medium">🇬🇷 Made in Greece</span></div>
          </div>
          <p className="text-gray-500 text-sm">Ανεπτυγμένο από την PROTOS AI Agency — Ελληνική εταιρεία AI με έδρα τη Θεσσαλονίκη</p>
        </div>
      </section>

      <section id="pricing" className="py-20 px-4 bg-slate-50">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 greek-font mb-4">Απλή Τιμολόγηση</h2>
            <p className="text-lg text-gray-500">Ξεκινήστε δωρεάν — αναβαθμίστε όταν είστε έτοιμοι</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 border-gray-200 hover:border-slate-400 transition-colors">
              <CardHeader className="text-center pb-2"><CardTitle className="greek-font text-xl">Δοκιμαστικό</CardTitle><div className="text-4xl font-bold text-gray-900 mt-4">Δωρεάν</div><CardDescription>14 ημέρες πλήρης πρόσβαση</CardDescription></CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />AI Νομική Αναζήτηση</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Σύνταξη Εγγράφων</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />10 AI ερωτήσεις/ημέρα</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Χωρίς κάρτα</div>
                </div>
                <Button onClick={() => setIsRegisterModalOpen(true)} className="w-full mt-4" variant="outline">Ξεκινήστε Δωρεάν</Button>
              </CardContent>
            </Card>
            <Card className="border-2 border-slate-900 shadow-xl relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2"><Badge className="bg-slate-900">Δημοφιλές</Badge></div>
              <CardHeader className="text-center pb-2"><CardTitle className="greek-font text-xl">Basic</CardTitle><div className="text-4xl font-bold text-gray-900 mt-4">€29<span className="text-lg font-normal text-gray-500">/μήνα</span></div><CardDescription>Για δικηγόρους</CardDescription></CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />AI Νομική Αναζήτηση</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Απεριόριστες ερωτήσεις</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Σύνταξη Εγγράφων</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />AI Insights</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />3.5M+ Νομικά Έγγραφα</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Email Υποστήριξη</div>
                </div>
                <Button onClick={() => setIsRegisterModalOpen(true)} className="w-full mt-4 bg-slate-900 hover:bg-slate-800 text-white">Ξεκινήστε Τώρα</Button>
              </CardContent>
            </Card>
            <Card className="border-2 border-gray-200 hover:border-slate-400 transition-colors">
              <CardHeader className="text-center pb-2"><CardTitle className="greek-font text-xl">Premium</CardTitle><div className="text-4xl font-bold text-gray-900 mt-4">€99<span className="text-lg font-normal text-gray-500">/μήνα</span></div><CardDescription>Πλήρες CRM Γραφείου</CardDescription></CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Όλα τα Basic</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Πελάτες & Υποθέσεις</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />AADE myDATA Σύνδεση</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Τιμολόγηση & Χρονοχρέωση</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Σύγκρουση Συμφερόντων</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Προθεσμίες & Priority Support</div>
                </div>
                <Button onClick={() => setIsRegisterModalOpen(true)} className="w-full mt-4" variant="outline">Ξεκινήστε Τώρα</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>


      {/* FAQ Section */}
      <section className="py-20 px-4 bg-gray-50" id="faq">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 greek-font mb-12">Συχνές Ερωτήσεις</h2>
          <div className="space-y-4">
            {[
              {q: "Τι είναι η ΝΟΜΟΚΡΑΤΙΑ;", a: "Η ΝΟΜΟΚΡΑΤΙΑ είναι η πρώτη ελληνική AI πλατφόρμα που συνδυάζει νομική αναζήτηση σε 3.5M+ έγγραφα με πλήρες CRM διαχείρισης δικηγορικού γραφείου. Αναπτύχθηκε από την PROTOS AI Agency IKE στη Θεσσαλονίκη."},
              {q: "Παρέχει η ΝΟΜΟΚΡΑΤΙΑ νομικές συμβουλές;", a: "Όχι. Η ΝΟΜΟΚΡΑΤΙΑ παρέχει αποκλειστικά πληροφοριακές υπηρεσίες και εργαλεία για επαγγελματίες δικηγόρους. Δεν υποκαθιστά τη νομική συμβουλευτική σύμφωνα με τον Ν. 4194/2013 (Κώδικας Δικηγόρων)."},
              {q: "Ποια νομικά έγγραφα περιλαμβάνει η βάση δεδομένων;", a: "Περιλαμβάνει 3.5M+ έγγραφα: αποφάσεις Διαύγειας (3M+), νομοθεσία EUR-Lex (41K+), αποφάσεις Αρείου Πάγου, Συμβουλίου Επικρατείας, Διοικητικών Δικαστηρίων, εγκυκλίους ΑΑΔΕ, ΦΕΚ, κοινοβουλευτικούς νόμους και πολλά ακόμη."},
              {q: "Πόσο κοστίζει η ΝΟΜΟΚΡΑΤΙΑ;", a: "Προσφέρουμε 14 ημέρες δωρεάν δοκιμή χωρίς κάρτα. Μετά: Basic €29/μήνα (AI αναζήτηση, σύνταξη εγγράφων, AI Insights) ή Premium €99/μήνα (+ πλήρες CRM: πελάτες, AADE, τιμολόγηση, προθεσμίες, conflict check). Όλες οι τιμές πλέον ΦΠΑ."},
              {q: "Τι περιλαμβάνει το CRM;", a: "Το CRM περιλαμβάνει Πελάτες, Υποθέσεις, Προθεσμίες, Χρονοχρέωση, Τιμολόγηση με AADE/myDATA και Σύγκρουση Συμφερόντων — διαθέσιμο στο Premium πλάνο (€99/μήνα). Το Basic πλάνο (€29/μήνα) περιλαμβάνει AI Νομολογία, Σύνταξη Εγγράφων και AI Insights."},
              {q: "Είναι ασφαλή τα δεδομένα μου;", a: "Απολύτως. Χρησιμοποιούμε κρυπτογράφηση TLS/SSL, EU servers (Hetzner, Germany), συμμόρφωση GDPR και δεν μοιραζόμαστε δεδομένα με τρίτους για marketing. Είμαστε EU AI Pact Signatory."},
              {q: "Υποστηρίζει τιμολόγηση μέσω ΑΑΔΕ;", a: "Ναι. Η ΝΟΜΟΚΡΑΤΙΑ ενσωματώνεται απευθείας με το myDATA της ΑΑΔΕ για αυτόματη έκδοση και διαβίβαση παραστατικών."},
              {q: "Ποια AI χρησιμοποιεί η πλατφόρμα;", a: "Χρησιμοποιούμε Claude (Anthropic) — ένα από τα πιο προηγμένα AI μοντέλα παγκοσμίως — ειδικά βελτιστοποιημένο για ελληνικά νομικά κείμενα."},
              {q: "Μπορώ να δοκιμάσω δωρεάν;", a: "Ναι! Εγγραφείτε τώρα και αποκτήστε 14 ημέρες πλήρη πρόσβαση σε όλες τις λειτουργίες, χωρίς πιστωτική κάρτα και χωρίς δεσμεύσεις."},
              {q: "Τι σημαίνει 'Σύγκρουση Συμφερόντων';", a: "Το Conflict Check ελέγχει αυτόματα αν ένας νέος πελάτης ή υπόθεση συγκρούεται με υπάρχοντες πελάτες σας — κρίσιμο για τη δεοντολογία του δικηγόρου."}
            ].map((item, i) => (
              <details key={i} className="group bg-white border border-gray-200 rounded-xl overflow-hidden">
                <summary className="flex justify-between items-center p-5 cursor-pointer hover:bg-gray-50 transition-colors">
                  <span className="font-semibold text-slate-900 greek-font pr-4">{item.q}</span>
                  <span className="text-slate-400 group-open:rotate-180 transition-transform text-xl flex-shrink-0">▼</span>
                </summary>
                <div className="px-5 pb-5 text-gray-600 border-t border-gray-100 pt-4">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

            <section className="py-20 px-4 bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-4xl font-bold text-white greek-font mb-6">Έτοιμοι να αλλάξετε τον τρόπο που δουλεύετε;</h2>
          <p className="text-xl text-slate-300 mb-10">14 ημέρες δωρεάν. Χωρίς κάρτα. Χωρίς δεσμεύσεις.</p>
          <Button onClick={() => setIsRegisterModalOpen(true)} className="bg-white text-slate-900 hover:bg-slate-100 text-lg px-10 py-6 rounded-xl shadow-lg">Ξεκινήστε Δωρεάν Τώρα →</Button>
          
        </div>
      </section>

      <footer className="bg-white text-slate-800 py-12 px-4 border-t border-slate-200">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8">
            <div><img src="https://customer-assets.emergentagent.com/job_ailegalpro/artifacts/wofbxh36_Untitled%20design%20%2821%29.png" alt="ΝΟΜΟΚΡΑΤΙΑ Logo" className="h-12 w-auto mb-4" /><p className="text-slate-500 text-sm">Η επαγγελματική νομική πλατφόρμα σας για σύγχρονα δικηγορικά γραφεία.</p></div>
            <div><h4 className="font-semibold mb-4 greek-font text-slate-800">Υποστήριξη</h4><div className="text-slate-600"><p className="flex items-center"><span className="mr-2">📧</span>info@nomokratia.ai</p></div></div>
            <div><h4 className="font-semibold mb-4 greek-font text-slate-800">Νομικά</h4><ul className="space-y-2 text-slate-600"><li><PrivacyPolicyModal /></li><li><TermsOfServiceModal /></li><li><a href="mailto:info@nomokratia.ai" className="text-slate-500 hover:text-slate-900 transition-colors">📧 Επικοινωνία</a></li></ul></div>
          </div>
          <Separator className="my-8 bg-slate-200" />
          <div className="max-w-3xl mx-auto mb-6">
            <div style={{background:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:10,padding:'12px 16px',fontSize:13,color:'#1E3A8A',fontWeight:500,lineHeight:1.6,textAlign:'center'}}>
              🤖 Η πλατφόρμα ΝΟΜΟΚΡΑΤΙΑ χρησιμοποιεί τεχνητή νοημοσύνη (AI). Το παραγόμενο περιεχόμενο είναι ενημερωτικό, δημιουργείται από AI και απαιτεί έλεγχο από αδειούχο δικηγόρο. (Κανονισμός ΕΕ 2024/1689 – Άρθρο 50)
            </div>
          </div>
          <div className="text-center text-slate-500"><p>&copy; 2026 ΝΟΜΟΚΡΑΤΙΑ by PROTOS AI Agency IKE. Όλα τα δικαιώματα διατηρούνται.</p><p className="text-slate-400 text-xs mt-2">PROTOS AI Agency IKE · Αρχαιολογικού Μουσείου 17, 54640 Θεσσαλονίκη · ΑΦΜ 802208607 · ΓΕΜΗ 172146906000</p></div>
        </div>
      </footer>
    </div>
  );
};

// Dashboard Component
const Dashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [consultations, setConsultations] = useState([]);
  const [newQuestion, setNewQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (activeTab === "consultations") {
      fetchConsultations();
    }
  }, [activeTab]);

  const fetchConsultations = async () => {
    try {
      const response = await axios.get(`${API}/legal/consultations`);
      setConsultations(response.data);
    } catch (error) {
      console.error('Αποτυχία φόρτωσης συμβουλών:', error);
    }
  };

  const submitQuestion = async () => {
    if (!newQuestion.trim()) return;
    
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/legal/consultation`, {
        question: newQuestion
      });
      
      setConsultations([response.data, ...consultations]);
      setNewQuestion("");
    } catch (error) {
      setError(error.response?.data?.detail || "Σφάλμα κατά τη δημιουργία συμβουλής");
    } finally {
      setLoading(false);
    }
  };

  const getTrialDaysLeft = () => {
    if (!user.trial_ends_at) return 0;
    const trialEnd = new Date(user.trial_ends_at);
    const now = new Date();
    const diffTime = trialEnd - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_ailegalpro/artifacts/wofbxh36_Untitled%20design%20%2821%29.png" 
                alt="ΝΟΜΟΚΡΑΤΙΑ Logo" 
                className="h-12 w-auto"
              />
            </div>
            
            <div className="flex items-center space-x-3">
              <Avatar>
                <AvatarImage src={user.picture} />
                <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium text-slate-700">{user.name}</span>
              {user.subscription_status === "trial" && (
                <Badge variant="outline" className="text-orange-600 border-orange-600">
                  {getTrialDaysLeft()} ημέρες δοκιμής
                </Badge>
              )}
              {user.subscription_status === "active" && (
                <Badge className="bg-green-600">
                  <Crown className="h-4 w-4 mr-1" />
                  {user.subscription_plan}
                </Badge>
              )}
              <Button variant="ghost" size="sm" onClick={onLogout}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-6 mb-6">
            <TabsTrigger value="dashboard">Πίνακας</TabsTrigger>
            <TabsTrigger value="consultations">Συμβουλή ΤΝ</TabsTrigger>
            <TabsTrigger value="clients">Πελάτες</TabsTrigger>
            <TabsTrigger value="documents">Έγγραφα</TabsTrigger>
            <TabsTrigger value="settings">Ρυθμίσεις</TabsTrigger>
            <TabsTrigger value="syntaxi">✍️ Σύνταξη</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            {user.subscription_status === "trial" && (
              <Alert className="border-orange-200 bg-orange-50">
                <Sparkles className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-orange-800">
                  Έχετε {getTrialDaysLeft()} ημέρες στη δωρεάν δοκιμαστική περίοδο. 
                  Αναβαθμίστε τώρα για απεριόριστη πρόσβαση!
                </AlertDescription>
              </Alert>
            )}

            <div className="grid md:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center greek-font">
                    <MessageSquare className="h-5 w-5 mr-2 text-slate-900" />
                    Συμβουλές
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-800">
                    {consultations.length}
                  </div>
                  <p className="text-slate-600">Πραγματοποιημένες συμβουλές</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center greek-font">
                    <Users className="h-5 w-5 mr-2 text-green-600" />
                    Πελάτες
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-800">0</div>
                  <p className="text-slate-600">Ενεργοί πελάτες</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center greek-font">
                    <CreditCard className="h-5 w-5 mr-2 text-purple-600" />
                    Κατάσταση Συνδρομής
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-xl font-bold text-slate-800 capitalize">
                    {user.subscription_status === "trial" ? "Δοκιμαστική" : user.subscription_status}
                  </div>
                  <p className="text-slate-600">
                    {user.subscription_plan ? `${user.subscription_plan} Πλάνο` : "Δωρεάν δοκιμαστική περίοδος"}
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="consultations" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="greek-font">Νέα Νομική Συμβουλή</CardTitle>
                <CardDescription>
                  Θέστε τη νομική σας ερώτηση και λάβετε τεκμηριωμένη απάντηση 
                  βασισμένη σε ισχύοντες νόμους και αποφάσεις.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder="Περιγράψτε τη νομική σας υπόθεση ή την ερώτησή σας..."
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                  rows={4}
                />
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <Button onClick={submitQuestion} disabled={loading || !newQuestion.trim()}>
                  {loading ? "Δημιουργία συμβουλής..." : "Αίτηση συμβουλής"}
                </Button>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold greek-font">Προηγούμενες Συμβουλές</h3>
              {consultations.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-8 text-slate-500">
                    Δεν υπάρχουν συμβουλές ακόμα.
                  </CardContent>
                </Card>
              ) : (
                consultations.map((consultation) => (
                  <Card key={consultation.id} className="consultation-card">
                    <CardHeader>
                      <CardTitle className="text-lg">{consultation.question}</CardTitle>
                      <CardDescription>
                        {new Date(consultation.created_at).toLocaleString('el-GR')}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-40 w-full border rounded p-4 mb-4">
                        <div className="whitespace-pre-wrap text-sm consultation-answer">
                          {consultation.answer}
                        </div>
                      </ScrollArea>
                      {consultation.sources.length > 0 && (
                        <div className="source-citation">
                          <h4 className="font-semibold mb-2 greek-font">Πηγές:</h4>
                          <ul className="list-disc list-inside text-sm text-slate-600">
                            {consultation.sources.map((source, index) => (
                              <li key={index}>{source}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="clients">
            <Card>
              <CardHeader>
                <CardTitle className="greek-font">Διαχείριση Πελατών</CardTitle>
                <CardDescription>
                  Διαχειριστείτε τους πελάτες σας και τις υποθέσεις τους (Έρχεται Σύντομα)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-slate-500">
                  <Users className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <p>Αυτή η λειτουργία θα είναι διαθέσιμη σύντομα.</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <CardTitle className="greek-font">Διαχείριση Εγγράφων</CardTitle>
                <CardDescription>
                  Οργανώστε τα νομικά σας έγγραφα (Έρχεται Σύντομα)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-slate-500">
                  <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <p>Αυτή η λειτουργία θα είναι διαθέσιμη σύντομα.</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle className="greek-font">Ρυθμίσεις Λογαριασμού</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium greek-font">Όνομα</label>
                    <Input value={user.name} disabled />
                  </div>
                  <div>
                    <label className="text-sm font-medium">E-Mail</label>
                    <Input value={user.email} disabled />
                  </div>
                </div>
                
                <Separator />
                
                <div>
                  <h4 className="font-semibold mb-2 greek-font">Συνδρομή</h4>
                  <p className="text-sm text-slate-600 mb-4">
                    Κατάσταση: {user.subscription_status === "trial" ? "Δοκιμαστική περίοδος" : user.subscription_status}
                    {user.subscription_plan && ` (${user.subscription_plan})`}
                  </p>
                  
                  {user.subscription_status === "trial" && (
                    <Button onClick={() => window.location.href = "/"}>
                      Αναβάθμιση τώρα
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="syntaxi">
            <Syntaxi token={token} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Payment Success/Cancel Components
const PaymentSuccess = () => {
  const [status, setStatus] = useState("checking");
  
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setStatus("success");
        setTimeout(() => {
          window.location.href = "/dashboard";
        }, 3000);
      } else if (response.data.status === "expired") {
        setStatus("expired");
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {status === "checking" && <CardTitle className="greek-font">Επεξεργασία πληρωμής...</CardTitle>}
          {status === "success" && (
            <>
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <CardTitle className="text-green-700 greek-font">Επιτυχής πληρωμή!</CardTitle>
            </>
          )}
          {status === "error" && <CardTitle className="text-red-700 greek-font">Σφάλμα πληρωμής</CardTitle>}
          {status === "expired" && <CardTitle className="text-orange-700 greek-font">Η συνεδρία έληξε</CardTitle>}
          {status === "timeout" && <CardTitle className="text-red-700 greek-font">Λήξη χρόνου</CardTitle>}
        </CardHeader>
        <CardContent className="text-center">
          {status === "checking" && <p>Παρακαλώ περιμένετε όσο επιβεβαιώνουμε την πληρωμή σας...</p>}
          {status === "success" && <p>Ευχαριστούμε! Θα μετακινηθείτε σύντομα...</p>}
          {status === "error" && <p>Υπήρξε πρόβλημα με την πληρωμή. Παρακαλώ δοκιμάστε ξανά.</p>}
          {status === "expired" && <p>Η συνεδρία πληρωμής έληξε. Παρακαλώ δοκιμάστε ξανά.</p>}
          {status === "timeout" && <p>Η επιβεβαίωση πληρωμής διαρκεί περισσότερο από το αναμενόμενο.</p>}
        </CardContent>
      </Card>
    </div>
  );
};

const PaymentCancel = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center">
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <CardTitle className="greek-font">Ακύρωση πληρωμής</CardTitle>
      </CardHeader>
      <CardContent className="text-center space-y-4">
        <p>Η πληρωμή ακυρώθηκε. Μπορείτε να επιστρέψετε ανά πάσα στιγμή για να ολοκληρώσετε τη συνδρομή.</p>
        <Button onClick={() => window.location.href = "/"}>
          Επιστροφή στην αρχική σελίδα
        </Button>
      </CardContent>
    </Card>
  </div>
);

// Main App Component
function App() {
  const [showAdmin, setShowAdmin] = useState(false);
  const { user, loading, login, logout } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_ailegalpro/artifacts/wofbxh36_Untitled%20design%20%2821%29.png" 
            alt="ΝΟΜΟΚΡΑΤΙΑ Logo" 
            className="h-16 w-auto mx-auto mb-4 animate-pulse"
          />
          <p className="text-slate-600 greek-font">Φόρτωση ΝΟΜΟΚΡΑΤΙΑ...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/subscription/success" element={<PaymentSuccess />} />
          <Route path="/subscription/cancel" element={<PaymentCancel />} />
          <Route path="/learn-more" element={<LearnMorePage />} />
          <Route 
            path="/dashboard" 
            element={user ? <CRMDashboard user={user} onLogout={logout} /> : <Navigate to="/" />} 
          />
          <Route 
            path="/profile" 
            element={user ? <Navigate to="/dashboard" /> : <Navigate to="/" />} 
          />
          <Route 
            path="/" 
            element={user ? <Navigate to="/dashboard" /> : <LandingPage onLogin={login} />} 
          />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/crm-demo" element={<CRMProfessional />} />
</Routes>
      {/* Admin Dashboard */}
      {localStorage.getItem("nomokratia_user") && JSON.parse(localStorage.getItem("nomokratia_user") || "{}").email === "info@ai-agency.gr" && (
        <button onClick={()=>setShowAdmin(true)} style={{position:"fixed",bottom:20,right:20,padding:"10px 16px",borderRadius:10,background:"#1E293B",color:"#FFF",border:"none",fontSize:12,fontWeight:700,cursor:"pointer",zIndex:9998,boxShadow:"0 4px 12px rgba(0,0,0,0.2)"}}>
          Admin
        </button>
      )}
      {showAdmin && <AdminDashboard onClose={()=>setShowAdmin(false)} />}
      </BrowserRouter>
    </div>


    );
}

export default App;